/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { select, call } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  Asset,
  RecordingOption,
  AssetOptionType,
  RecordingOptionType,
  Recording,
  RecordingsUtils,
  RecordingStatus,
  ContentTagsUtils,
  TimeUtils,
  Program,
  OptionUtils,
  SeriesRecording,
  SeriesRecordingType,
  AssetOption,
  Channel,
  AssetUtils,
  EntryPoint,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
import { Selectors as SingleRecordingsSelectors } from '../../recordings/singleRecordings';
import { Selectors as SeriesRecordingsSelectors } from '../../recordings/seriesRecordings';
import Seasons, { SeasonsData } from '../seasons';
import translate from '../../../../../services/i18n/translate';

interface AllowedOptionsByEntryPoint {
  [key: string]: RecordingOptionType[];
}

const ALLOWED_OPTIONS_BY_ENTRY_POINT: AllowedOptionsByEntryPoint = {
  [EntryPoint.WATCH_NEXT]: [
    RecordingOptionType.RECORD_SINGLE,
    RecordingOptionType.RECORD_SERIES,
    RecordingOptionType.CANCEL_SINGLE,
    RecordingOptionType.CANCEL_EPISODE,
    RecordingOptionType.CANCEL_SERIES,
  ],
  [EntryPoint.RECORDINGS]: [
    RecordingOptionType.RECORD_SINGLE,
    RecordingOptionType.RECORD_SERIES,
    RecordingOptionType.CANCEL_SINGLE,
    RecordingOptionType.CANCEL_EPISODE,
    RecordingOptionType.CANCEL_SERIES,
    RecordingOptionType.DELETE_SINGLE,
    RecordingOptionType.DELETE_EPISODE,
    RecordingOptionType.DELETE_SERIES,
  ],
  [EntryPoint.SCHEDULED_RECORDINGS]: [
    RecordingOptionType.RECORD_SINGLE,
    RecordingOptionType.RECORD_SERIES,
    RecordingOptionType.CANCEL_SINGLE,
    RecordingOptionType.CANCEL_EPISODE,
    RecordingOptionType.CANCEL_SERIES,
    RecordingOptionType.DELETE_SINGLE,
    RecordingOptionType.DELETE_EPISODE,
    RecordingOptionType.DELETE_SERIES,
  ],
  [EntryPoint.PURCHASES]: [],
};

function buildRecordOption(asset: Asset, title: string, channel: Channel): RecordingOption {
  return {
    key: `${AssetOptionType.RECORDING}-${RecordingOptionType.RECORD_SINGLE}-${asset.id}`,
    type: AssetOptionType.RECORDING,
    recordingOptionType: RecordingOptionType.RECORD_SINGLE,
    title,
    asset,
    channel,
  };
}

function* getRecordOption(
  infoAsset: Asset,
  clones: Asset[],
  channels: Channel[],
  singleRecordings: Recording[]
): SagaIterator<RecordingOption[]> {
  const options: RecordingOption[] = [];

  if (
    AssetUtils.isRecording(infoAsset) &&
    RecordingsUtils.getRecordingStatus(infoAsset) !== RecordingStatus.COMPLETED
  ) {
    const foundedRecordings = singleRecordings.filter((e) => e.internalId === infoAsset.internalId);

    if (foundedRecordings.length === 0) {
      const program: Program | undefined = yield call(Ducks.Epg.Live.getLiveProgram, infoAsset.channelEpgId);
      const relatedProgram = program && program.internalId === infoAsset.internalId ? program : undefined;
      const relatedChannel = channels.find(
        (channel) => channel.epgId === infoAsset.channelEpgId && channel.isSubscribed && channel.streams.length
      );

      if (relatedProgram && relatedChannel) {
        options.push(buildRecordOption(relatedProgram, relatedChannel.title, relatedChannel));
      }
    }
  } else {
    const clonePrograms = clones.filter(
      (clone) =>
        AssetUtils.isEpg(clone) && ContentTagsUtils.isRecordingEnabled(clone) && TimeUtils.isAfterNow(clone.endDate)
    ) as Program[];

    const aggregatedClonePrograms = OptionUtils.aggregateByChannel(clonePrograms, true);

    const foundedRecordings = singleRecordings.filter((r) =>
      aggregatedClonePrograms.find((p) => p.internalId === r.internalId)
    );

    // If it finds a single recording for the asset, no mather the channel it is, the record option should not show
    if (foundedRecordings.length === 0) {
      aggregatedClonePrograms.forEach((cloneProgram) => {
        if (TimeUtils.isAfterNow(cloneProgram.endDate)) {
          const relatedChannel = channels.find(
            (channel) => channel.epgId === cloneProgram.channelEpgId && channel.isSubscribed && channel.streams.length
          );

          if (relatedChannel) {
            options.push(buildRecordOption(cloneProgram, relatedChannel.title, relatedChannel));
          }
        }
      });
    }
  }

  return options;
}

function buildRecordSeriesOption(
  channel: Channel,
  asset: Asset,
  foundedSeriesRecordings: SeriesRecording[],
  seasonsData?: SeasonsData
): RecordingOption {
  const options: RecordingOption[] = [];

  const seasons = Seasons.getSeasonNumbers(seasonsData);

  if (seasons && seasons.length > 0 && foundedSeriesRecordings.length < seasons.length) {
    options.push({
      key: `${AssetOptionType.RECORDING}-${RecordingOptionType.RECORD_SERIES}-${asset.id}`,
      type: AssetOptionType.RECORDING,
      recordingOptionType: RecordingOptionType.RECORD_SERIES,
      title: translate['buttons.recordSeries'],
      asset,
      channel,
    });
  }

  return {
    key: `${AssetOptionType.RECORDING}-${RecordingOptionType.RECORD_SERIES}-${asset.id}`,
    type: AssetOptionType.RECORDING,
    recordingOptionType: RecordingOptionType.RECORD_SERIES,
    title: channel.title,
    asset,
    channel,
    children: options.length > 0 ? options : undefined,
    multiOptions: options.length > 0 ? true : undefined,
  };
}

function getRecordSeriesOption(
  representativeAsset: Asset,
  clones: Asset[],
  channels: Channel[],
  serieRecordings: SeriesRecording[],
  seasons?: SeasonsData
): RecordingOption[] {
  if (!AssetUtils.hasValidSeriesId(representativeAsset)) {
    return [];
  }

  const options: RecordingOption[] = [];

  if (
    AssetUtils.isRecording(representativeAsset) &&
    RecordingsUtils.getRecordingStatus(representativeAsset) !== RecordingStatus.COMPLETED
  ) {
    const foundedSeriesRecordings = serieRecordings.filter((e) => representativeAsset.seriesId === e.seriesId);

    const typeSeriesSeriesRecordings = foundedSeriesRecordings.find((e) => e.type === SeriesRecordingType.SERIES);

    if (!typeSeriesSeriesRecordings) {
      const relatedChannel = channels.find(
        (channel) =>
          channel.epgId === representativeAsset.channelEpgId && channel.isSubscribed && channel.streams.length
      );

      if (relatedChannel) {
        options.push(buildRecordSeriesOption(relatedChannel, representativeAsset, foundedSeriesRecordings, seasons));
      }
    }
  } else {
    // If it doesn't exists it will get all the available sources to be presented
    const clonePrograms = clones.filter(
      (clone) => AssetUtils.isEpg(clone) && ContentTagsUtils.isRecordingEnabled(clone)
    ) as Program[];

    const aggregatedClonePrograms = OptionUtils.aggregateByChannel(clonePrograms);

    const foundedSeriesRecordings = serieRecordings.filter((r) =>
      aggregatedClonePrograms.find((p) => p.channelEpgId === r.epgChannelId && p.seriesId === r.seriesId)
    );

    const typeSeriesSeriesRecordings = foundedSeriesRecordings.find((e) => e.type === SeriesRecordingType.SERIES);

    if (!typeSeriesSeriesRecordings) {
      aggregatedClonePrograms.forEach((cloneProgram) => {
        // if (TimeUtils.isAfterNow(cloneProgram.endDate)) {
        const relatedChannel = channels.find(
          (channel) => channel.epgId === cloneProgram.channelEpgId && channel.isSubscribed && channel.streams.length
        );

        if (relatedChannel) {
          options.push(buildRecordSeriesOption(relatedChannel, cloneProgram, foundedSeriesRecordings, seasons));
        }
        // }
      });
    }
  }

  return options;
}

function buildCancelOption(
  optionType: RecordingOptionType,
  title: string,
  asset: Asset,
  seriesRecording?: SeriesRecording,
  singleRecording?: Recording
): RecordingOption {
  return {
    key: `${AssetOptionType.RECORDING}-${optionType}-${asset.id}`,
    type: AssetOptionType.RECORDING,
    recordingOptionType: optionType,
    title,
    asset,
    seriesRecording,
    singleRecording,
  };
}

function buildCancelOptionFromSingleRecording(
  asset: Asset,
  seriesRecordings: SeriesRecording[],
  singleRecording?: Recording
): RecordingOption | undefined {
  if (singleRecording) {
    const singleRecordingStatus = RecordingsUtils.getRecordingStatus(singleRecording);

    if (singleRecordingStatus === RecordingStatus.ON_GOING || singleRecordingStatus === RecordingStatus.SCHEDULED) {
      if (AssetUtils.hasValidSeriesId(asset)) {
        const serieRecording = seriesRecordings.find(
          (e) =>
            e.seriesId === singleRecording.seriesId &&
            e.epgChannelId === singleRecording.channelEpgId &&
            e.type === SeriesRecordingType.SERIES
        );

        return buildCancelOption(
          RecordingOptionType.CANCEL_EPISODE,
          translate.buttonCancelEpisode,
          asset,
          serieRecording,
          singleRecording
        );
      }

      return buildCancelOption(
        RecordingOptionType.CANCEL_SINGLE,
        translate.buttonCancelRecording,
        asset,
        undefined,
        singleRecording
      );
    }
  }

  return undefined;
}

function getCancelOption(
  infoAsset: Asset,
  clones: Asset[],
  singleRecordings: Recording[],
  seriesRecordings: SeriesRecording[]
): RecordingOption | undefined {
  if (AssetUtils.isRecording(infoAsset)) {
    const singleRecording = singleRecordings.find((e) => e.internalId === infoAsset.internalId);
    return buildCancelOptionFromSingleRecording(infoAsset, seriesRecordings, singleRecording);
  }
  const clonePrograms = clones.filter(
    (clone) => AssetUtils.isEpg(clone) && ContentTagsUtils.isRecordingEnabled(clone)
  ) as Program[];

  const aggregatedClonePrograms = OptionUtils.aggregateByChannel(clonePrograms);

  const singleRecording = singleRecordings.find((e) =>
    aggregatedClonePrograms.find((p) => e.internalId === p.internalId)
  );

  return buildCancelOptionFromSingleRecording(infoAsset, seriesRecordings, singleRecording);
}

function getCancelSeriesOption(infoAsset: Asset, serieRecordings: SeriesRecording[]): RecordingOption[] {
  const options: RecordingOption[] = [];

  if (AssetUtils.hasValidSeriesId(infoAsset)) {
    const seriesRecording = serieRecordings.find(
      (r) => r.seriesId === infoAsset.seriesId && r.type === SeriesRecordingType.SERIES
    );

    if (seriesRecording) {
      options.push(
        buildCancelOption(
          RecordingOptionType.CANCEL_SERIES,
          translate['buttons.cancelSeriesRecording'],
          infoAsset,
          seriesRecording
        )
      );
    }
  }

  return options;
}

function buildDeleteOption(
  optionType: RecordingOptionType,
  asset: Asset,
  title: string,
  singleRecording?: Recording,
  seasonNumber?: number,
  totalRecordings?: number,
  totalProtected?: number,
  totalSeasons?: number
): RecordingOption {
  return {
    key: `${AssetOptionType.RECORDING}-${optionType}-${asset.id}`,
    type: AssetOptionType.RECORDING,
    recordingOptionType: optionType,
    title,
    asset,
    singleRecording,
    seasonNumber,
    totalRecordings,
    totalProtected,
    totalSeasons,
  };
}

function buildDeleteOptionFromSingleRecording(
  asset: Asset,
  singleRecording?: Recording,
  totalRecordings?: number,
  totalProtected?: number
): RecordingOption | undefined {
  if (singleRecording) {
    const singleRecordingStatus = RecordingsUtils.getRecordingStatus(singleRecording);

    if (singleRecordingStatus === RecordingStatus.COMPLETED) {
      if (AssetUtils.hasValidSeriesId(asset)) {
        return buildDeleteOption(
          RecordingOptionType.DELETE_EPISODE,
          asset,
          translate.buttonDeleteEpisode,
          singleRecording,
          undefined,
          totalRecordings,
          totalProtected
        );
      }

      return buildDeleteOption(
        RecordingOptionType.DELETE_SINGLE,
        asset,
        translate.buttonDelete,
        singleRecording,
        undefined,
        totalRecordings,
        totalProtected
      );
    }
  }

  return undefined;
}

function getDeleteOption(
  infoAsset: Asset,
  clones: Asset[],
  singleRecordings: Recording[]
): RecordingOption | undefined {
  let totalRecordings: number | undefined;
  let totalProtected: number | undefined;

  if (AssetUtils.hasValidSeriesId(infoAsset) && (AssetUtils.isEpg(infoAsset) || AssetUtils.isRecording(infoAsset))) {
    singleRecordings.forEach((recording) => {
      if (
        recording.seriesId === infoAsset.seriesId &&
        RecordingsUtils.getRecordingStatus(recording) === RecordingStatus.COMPLETED &&
        recording.seasonNumber &&
        AssetUtils.isValidSeasonNumber(recording.seasonNumber)
      ) {
        if (!totalRecordings) {
          if (!totalProtected) {
            totalProtected = 0;
          }

          totalRecordings = 1;
        } else {
          totalRecordings += 1;
        }

        if (recording.isAssetProtected) {
          if (!totalProtected) {
            totalProtected = 1;
          } else {
            totalProtected += 1;
          }
        }
      }
    });
  }

  if (AssetUtils.isRecording(infoAsset)) {
    const singleRecording = singleRecordings.find((e) => e.internalId === infoAsset.internalId);
    return buildDeleteOptionFromSingleRecording(infoAsset, singleRecording, totalRecordings, totalProtected);
  }

  const clonePrograms = clones.filter(
    (clone) => AssetUtils.isEpg(clone) && ContentTagsUtils.isRecordingEnabled(clone)
  ) as Program[];

  const aggregatedClonePrograms = OptionUtils.aggregateByChannel(clonePrograms);

  const singleRecording = singleRecordings.find((e) =>
    aggregatedClonePrograms.find((p) => e.internalId === p.internalId)
  );

  return buildDeleteOptionFromSingleRecording(infoAsset, singleRecording, totalRecordings, totalProtected);
}

function getDeleteSeriesOption(infoAsset: Asset, singleRecordings: Recording[]): RecordingOption[] {
  const options: RecordingOption[] = [];

  if (AssetUtils.hasValidSeriesId(infoAsset)) {
    const mapSeasonNumberToCompletedRecordings = new Map<number, { totalRecordings: number; totalProtected: number }>();

    singleRecordings.forEach((recording) => {
      if (
        recording.seriesId === infoAsset.seriesId &&
        RecordingsUtils.getRecordingStatus(recording) === RecordingStatus.COMPLETED &&
        recording.seasonNumber &&
        AssetUtils.isValidSeasonNumber(recording.seasonNumber)
      ) {
        const recordingsInfo = mapSeasonNumberToCompletedRecordings.get(recording.seasonNumber);

        if (recordingsInfo) {
          mapSeasonNumberToCompletedRecordings.set(recording.seasonNumber, {
            totalRecordings: recordingsInfo.totalRecordings + 1,
            totalProtected: recording.isAssetProtected
              ? recordingsInfo.totalProtected + 1
              : recordingsInfo.totalProtected,
          });
        } else {
          mapSeasonNumberToCompletedRecordings.set(recording.seasonNumber, {
            totalRecordings: 1,
            totalProtected: recording.isAssetProtected ? 1 : 0,
          });
        }
      }
    });

    let totalSeriesRecordings = 0;
    let totalSeriesProtected = 0;

    mapSeasonNumberToCompletedRecordings.forEach(({ totalRecordings, totalProtected }) => {
      totalSeriesRecordings += totalRecordings;
      totalSeriesProtected += totalProtected;
    });
    if (totalSeriesRecordings) {
      options.push(
        buildDeleteOption(
          RecordingOptionType.DELETE_SERIES,
          infoAsset,
          translate['buttons.deleteSeriesRecording'],
          undefined,
          undefined,
          totalSeriesRecordings,
          totalSeriesProtected,
          mapSeasonNumberToCompletedRecordings.size
        )
      );
    }
  }

  return options.sort((a, b) => (a.seasonNumber || 0) - (b.seasonNumber || 0));
}

function aggregateOptions(options: RecordingOption[], title: string): AssetOption | undefined {
  if (options.length === 1) {
    const [option] = options;
    return {
      ...option,
      title,
    };
  }

  if (options.length > 1) {
    return {
      key: `${AssetOptionType.RECORDING}`,
      type: AssetOptionType.RECORDING,
      title,
      children: options,
      multiOptions: true,
    };
  }

  return undefined;
}

function isAllowedByEntryPoint(entryPoint: EntryPoint | undefined, recordingOptionType: RecordingOptionType): boolean {
  if (!entryPoint) {
    return true;
  }

  if (Object.keys(ALLOWED_OPTIONS_BY_ENTRY_POINT).includes(entryPoint)) {
    const allowedOptions = ALLOWED_OPTIONS_BY_ENTRY_POINT[entryPoint];
    return allowedOptions.includes(recordingOptionType);
  }

  return true;
}

function* getOptions(
  entryPoint: EntryPoint | undefined,
  asset: Asset,
  clones: Asset[],
  channels: Channel[],
  seasons?: SeasonsData
): SagaIterator<AssetOption[]> {
  const options: AssetOption[] = [];

  const serieRecordings: SeriesRecording[] = yield select(SeriesRecordingsSelectors.getRecordings);
  const singleRecordings: Recording[] = yield select(SingleRecordingsSelectors.getRecordings);

  if (isAllowedByEntryPoint(entryPoint, RecordingOptionType.RECORD_SERIES)) {
    const recordSeriesOptions: RecordingOption[] = yield call(
      getRecordSeriesOption,
      asset,
      clones,
      channels,
      serieRecordings,
      seasons
    );
    const recordSeriesOption = aggregateOptions(recordSeriesOptions, translate['buttons.recordSeries']);
    if (recordSeriesOption) {
      options.push(recordSeriesOption);
    }
  }

  if (isAllowedByEntryPoint(entryPoint, RecordingOptionType.CANCEL_SERIES)) {
    const cancelSeriesOptions: RecordingOption[] = yield call(getCancelSeriesOption, asset, serieRecordings);
    const cancelSeriesOption = aggregateOptions(cancelSeriesOptions, translate['buttons.cancelSeriesRecording']);
    if (cancelSeriesOption) {
      options.push(cancelSeriesOption);
    }
  }

  if (isAllowedByEntryPoint(entryPoint, RecordingOptionType.DELETE_SERIES)) {
    const deleteSeriesOptions: RecordingOption[] = yield call(getDeleteSeriesOption, asset, singleRecordings);
    const deleteSeriesOption = aggregateOptions(deleteSeriesOptions, translate['buttons.deleteSeriesRecording']);
    if (deleteSeriesOption) {
      options.push(deleteSeriesOption);
    }
  }

  if (isAllowedByEntryPoint(entryPoint, RecordingOptionType.RECORD_SINGLE)) {
    const isEpisode = AssetUtils.hasValidSeriesId(asset);
    const recordOptions: RecordingOption[] = yield call(getRecordOption, asset, clones, channels, singleRecordings);
    const recordOption = aggregateOptions(
      recordOptions,
      isEpisode ? translate['buttons.recordEpisode'] : translate['buttons.record']
    );
    if (recordOption) {
      options.push(recordOption);
    }
  }

  if (
    isAllowedByEntryPoint(entryPoint, RecordingOptionType.CANCEL_SINGLE) ||
    isAllowedByEntryPoint(entryPoint, RecordingOptionType.CANCEL_EPISODE)
  ) {
    const cancelOption: RecordingOption = yield call(getCancelOption, asset, clones, singleRecordings, serieRecordings);
    if (cancelOption) {
      options.push(cancelOption);
    }
  }

  if (
    isAllowedByEntryPoint(entryPoint, RecordingOptionType.DELETE_SINGLE) ||
    isAllowedByEntryPoint(entryPoint, RecordingOptionType.DELETE_EPISODE)
  ) {
    const deleteOption: RecordingOption = yield call(getDeleteOption, asset, clones, singleRecordings);
    if (deleteOption) {
      options.push(deleteOption);
    }
  }

  return options;
}

export default {
  getOptions,
};
